import { Fragment, useState, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, PencilIcon, EyeIcon, ArrowPathIcon, CodeBracketIcon, CubeIcon, SparklesIcon, PaperAirplaneIcon, UserIcon, LightBulbIcon } from '@heroicons/react/24/outline';
import ReactECharts from 'echarts-for-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiHelpers } from '../../services/api';
import { useDashboardStore } from '../../store/dashboardStore';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';
import VisualChartBuilder from '../charts/VisualChartBuilder';

const chartTypes = [
  { id: 'bar', name: 'Bar Chart', icon: '📊' },
  { id: 'line', name: 'Line Chart', icon: '📈' },
  { id: 'pie', name: 'Pie Chart', icon: '🥧' },
  { id: 'donut', name: 'Donut Chart', icon: '🍩' },
  { id: 'area', name: 'Area Chart', icon: '📉' },
  { id: 'scatter', name: 'Scatter Plot', icon: '🔵' },
  { id: 'heatmap', name: 'Heatmap', icon: '🔥' },
  { id: 'geo', name: 'Geo Map', icon: '🌍' },
  { id: 'table', name: 'Table', icon: '📋' },
  { id: 'kpi', name: 'KPI Card', icon: '🎯' }
];

export default function EditChartModal({ isOpen, onClose, chart, onChartUpdated }) {
  // Debug: Log props on every render
  console.log('[EditChartModal] Render - isOpen:', isOpen, 'chart:', chart);
  
  const [chartName, setChartName] = useState('');
  const [chartType, setChartType] = useState('');
  const [query, setQuery] = useState('');
  const [description, setDescription] = useState('');
  const [xAxisLabel, setXAxisLabel] = useState('');
  const [yAxisLabel, setYAxisLabel] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic'); // 'basic', 'query', 'preview', 'ai'
  const [previewData, setPreviewData] = useState(null);
  const [previewOption, setPreviewOption] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  
  // Visual builder state
  const [mode, setMode] = useState('sql'); // 'visual' or 'sql'
  const [schema, setSchema] = useState(null);
  const [sourceType, setSourceType] = useState('datasource');
  const [tableName, setTableName] = useState('');
  const [visualConfig, setVisualConfig] = useState(null);
  
  // AI Assistant state
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const aiMessagesEndRef = useRef(null);
  
  // Date filter configuration state
  const [dateFilterEnabled, setDateFilterEnabled] = useState(false);
  const [dateFilterColumn, setDateFilterColumn] = useState('');
  const [dateFilterColumnType, setDateFilterColumnType] = useState('STRING');
  const [dateFilterDefaultRange, setDateFilterDefaultRange] = useState('last30days');
  
  const { updateChart } = useDashboardStore();
  const { getCurrentCompanyId } = useAuthStore();
  
  // Initialize form with chart data
  useEffect(() => {
    console.log('[EditChartModal] useEffect - isOpen:', isOpen, 'chart:', chart?.name);
    if (chart && isOpen) {
      console.log('[EditChartModal] Initializing form with chart data');
      setChartName(chart.name || '');
      setChartType(chart.type || 'bar');
      setQuery(chart.query || '');
      setDescription(chart.description || '');
      setXAxisLabel(chart.chartConfig?.xAxis?.label || '');
      setYAxisLabel(chart.chartConfig?.yAxis?.label || '');
      setPreviewData(null);
      setPreviewOption(null);
      setPreviewError(null);
      setActiveTab('basic');
      setMode('sql');
      setVisualConfig(null);
      
      // Reset AI state
      setAiMessages([]);
      setAiInput('');
      setAiSuggestion(null);
      
      // Initialize date filter state
      setDateFilterEnabled(chart.dateFilterConfig?.enabled || false);
      setDateFilterColumn(chart.dateFilterConfig?.column || '');
      setDateFilterColumnType(chart.dateFilterConfig?.columnType || 'STRING');
      setDateFilterDefaultRange(chart.dateFilterConfig?.defaultRange || 'last30days');
      
      // Fetch schema for visual builder
      fetchSchemaForChart();
    }
  }, [chart, isOpen]);
  
  // Auto-scroll AI messages
  useEffect(() => {
    aiMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiMessages]);
  
  // Fetch schema for the chart's data source
  const fetchSchemaForChart = async () => {
    if (!chart) return;
    
    // Get companyId from chart or fallback to auth store
    let companyId = chart.companyId?._id || chart.companyId;
    if (!companyId) {
      // Fallback: try to get from auth store
      const authState = useAuthStore.getState();
      companyId = authState.getCurrentCompanyId();
    }
    if (!companyId) {
      console.warn('[EditChartModal] No companyId found');
      return;
    }
    
    try {
      const response = await apiHelpers.getChartSources({ companyId });
      const sources = response.data;
      
      console.log('[EditChartModal] Chart info:', {
        isMart: chart.isMart,
        martId: chart.martId,
        isWideView: chart.isWideView,
        wideViewName: chart.wideViewName,
        dataSourceId: chart.dataSourceId
      });
      console.log('[EditChartModal] Available sources:', {
        dataSources: sources.dataSources?.length,
        wideViews: sources.wideViews?.length,
        marts: sources.marts?.length
      });
      
      let sourceData = null;
      let type = 'datasource';
      let name = '';
      
      // Find the matching source
      if (chart.isMart && chart.martId) {
        const martId = (typeof chart.martId === 'object' ? chart.martId._id : chart.martId)?.toString();
        sourceData = sources.marts?.find(m => m.id?.toString() === martId);
        type = 'mart';
        name = sourceData?.tableName || sourceData?.dbtModelName || '';
        console.log('[EditChartModal] Looking for mart:', martId, 'Found:', !!sourceData);
      } else if (chart.isWideView && chart.wideViewName) {
        sourceData = sources.wideViews?.find(w => w.name === chart.wideViewName);
        type = 'wideview';
        name = sourceData?.tableName || sourceData?.dbtModelName || '';
        console.log('[EditChartModal] Looking for wideView:', chart.wideViewName, 'Found:', !!sourceData);
      } else if (chart.dataSourceId) {
        const dsId = (typeof chart.dataSourceId === 'object' ? chart.dataSourceId._id : chart.dataSourceId)?.toString();
        console.log('[EditChartModal] Available dataSource IDs:', sources.dataSources?.map(d => d.id?.toString()));
        sourceData = sources.dataSources?.find(d => d.id?.toString() === dsId);
        type = 'datasource';
        
        // If not found in ready sources but we have the dataSourceId object with details, use those
        if (!sourceData && typeof chart.dataSourceId === 'object' && chart.dataSourceId.bigQueryTable) {
          console.log('[EditChartModal] Using dataSourceId object directly:', chart.dataSourceId);
          // Fetch schema for this specific data source
          try {
            const schemaResponse = await apiHelpers.getDataSourceSchema(dsId);
            const schemaData = schemaResponse.data;
            console.log('[EditChartModal] Schema response:', schemaData);
            
            // Try multiple places where columns might be
            const columns = schemaData?.tables?.[0]?.columns || 
                          schemaData?.schema?.tables?.[0]?.columns || 
                          [];
            
            sourceData = {
              id: dsId,
              name: chart.dataSourceId.name,
              tableName: chart.dataSourceId.bigQueryTable,
              schema: columns
            };
            console.log('[EditChartModal] Fetched schema directly:', sourceData.schema?.length, 'columns');
          } catch (schemaError) {
            console.warn('[EditChartModal] Could not fetch schema:', schemaError.message);
          }
        }
        
        name = sourceData?.tableName || sourceData?.name || '';
        console.log('[EditChartModal] Looking for dataSource:', dsId, 'Found:', !!sourceData);
      }
      
      if (sourceData) {
        console.log('[EditChartModal] Found source:', sourceData);
        console.log('[EditChartModal] Schema columns:', sourceData.schema?.length || 0);
        setSchema({
          tables: [{
            name: sourceData.name || name,
            columns: sourceData.schema || []
          }]
        });
        setSourceType(type);
        setTableName(name);
      } else {
        console.warn('[EditChartModal] Source not found - chart may be AI-generated or source deleted');
        // For AI-generated charts or charts with no specific source, try to infer from query
        if (chart.query) {
          // Try to extract table name from query
          const fromMatch = chart.query.match(/FROM\s+[`']?[\w${}]+[`']?\.([`'\w]+)/i);
          if (fromMatch) {
            const inferredTable = fromMatch[1].replace(/[`']/g, '');
            console.log('[EditChartModal] Inferred table from query:', inferredTable);
            setTableName(inferredTable);
            
            // Try to find this table in available sources
            const allSources = [...(sources.dataSources || []), ...(sources.wideViews || []), ...(sources.marts || [])];
            const matchingSource = allSources.find(s => 
              s.tableName === inferredTable || 
              s.dbtModelName === inferredTable ||
              s.name === inferredTable
            );
            
            if (matchingSource && matchingSource.schema?.length > 0) {
              console.log('[EditChartModal] Found source by table name:', matchingSource);
              setSchema({
                tables: [{
                  name: matchingSource.name || inferredTable,
                  columns: matchingSource.schema || []
                }]
              });
              setSourceType(matchingSource.type === 'mart' ? 'mart' : matchingSource.type === 'wideview' ? 'wideview' : 'datasource');
            } else {
              // No matching source or source has no schema - fetch directly from BigQuery
              console.log('[EditChartModal] Fetching schema from BigQuery for table:', inferredTable);
              try {
                // Determine dataset type from table name
                let dataset = 'default';
                if (inferredTable.startsWith('mart_')) dataset = 'marts';
                else if (inferredTable.startsWith('wide_')) dataset = 'wide_views';
                else if (inferredTable.startsWith('stg_')) dataset = 'staging';
                
                const bqResponse = await apiHelpers.getBigQueryTableSchema({ 
                  tableName: inferredTable, 
                  dataset,
                  companyId 
                });
                
                if (bqResponse.data?.columns?.length > 0) {
                  console.log('[EditChartModal] Got schema from BigQuery:', bqResponse.data.columns.length, 'columns');
                  setSchema({
                    tables: [{
                      name: inferredTable,
                      columns: bqResponse.data.columns
                    }]
                  });
                  setSourceType(dataset === 'marts' ? 'mart' : dataset === 'wide_views' ? 'wideview' : 'datasource');
                } else {
                  console.warn('[EditChartModal] No columns found in BigQuery for table:', inferredTable);
                }
              } catch (bqError) {
                console.warn('[EditChartModal] Could not fetch schema from BigQuery:', bqError.message);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('[EditChartModal] Error fetching schema:', error);
    }
  };
  
  const handlePreview = async () => {
    if (!query.trim()) {
      toast.error('Please enter a query first');
      return;
    }
    
    setIsPreviewLoading(true);
    setPreviewError(null);
    
    try {
      // Use the chart data endpoint with the new query
      const response = await apiHelpers.previewChartQuery({
        chartId: chart._id,
        query: query,
        type: chartType
      });
      
      setPreviewData(response.data.data);
      setPreviewOption(response.data.echartsOption);
      setActiveTab('preview');
    } catch (error) {
      console.error('Preview error:', error);
      setPreviewError(error.response?.data?.error || 'Failed to preview chart. Please check your query syntax.');
    } finally {
      setIsPreviewLoading(false);
    }
  };
  
  const handleSave = async () => {
    if (!chartName.trim()) {
      toast.error('Please enter a chart name');
      return;
    }
    
    if (!query.trim()) {
      toast.error('Please enter a SQL query');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const updatedChart = await updateChart(chart._id, {
        name: chartName.trim(),
        type: chartType,
        query: query.trim(),
        description: description.trim(),
        chartConfig: {
          ...chart.chartConfig,
          xAxis: {
            ...chart.chartConfig?.xAxis,
            label: xAxisLabel.trim() || undefined
          },
          yAxis: {
            ...chart.chartConfig?.yAxis,
            label: yAxisLabel.trim() || undefined
          }
        },
        dateFilterConfig: {
          enabled: dateFilterEnabled,
          column: dateFilterColumn.trim() || undefined,
          columnType: dateFilterColumnType,
          defaultRange: dateFilterDefaultRange
        }
      });
      
      toast.success('Chart updated successfully!');
      onChartUpdated?.(updatedChart);
      onClose();
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.response?.data?.error || 'Failed to update chart');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleClose = () => {
    setActiveTab('basic');
    setPreviewData(null);
    setPreviewOption(null);
    setPreviewError(null);
    onClose();
  };
  
  // Handle query generated from visual builder
  const handleVisualQueryGenerated = (generatedQuery, config) => {
    setQuery(generatedQuery);
    setVisualConfig(config);
  };
  
  // AI Assistant handlers
  const handleAiSend = async () => {
    if (!aiInput.trim() || isAiLoading) return;
    
    const userMessage = aiInput.trim();
    setAiInput('');
    setAiMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsAiLoading(true);
    
    try {
      const companyId = getCurrentCompanyId();
      
      // Get dataSourceId from chart
      const dataSourceId = chart.dataSourceId?._id || chart.dataSourceId || 
                           (chart.isMart ? chart.martId?._id || chart.martId : null);
      
      const response = await apiHelpers.generateChartWithAI({
        message: userMessage,
        companyId,
        dataSourceId,
        currentChartType: chartType,
        existingConfig: chart.chartConfig,
        existingQuery: query,
        existingChartName: chartName,
        isEditMode: true
      });
      
      const { response: aiResponse, chartSuggestion, recommendedChartType } = response.data;
      
      // Clean up the response content - remove any raw JSON that might be displayed
      let cleanResponse = aiResponse || '';
      
      // If the response still contains raw JSON, try to clean it up
      if (cleanResponse.includes('"chartSuggestion"') || cleanResponse.includes('"response"')) {
        // Try to extract just the explanation part
        const jsonMatch = cleanResponse.match(/\{[\s\S]*"response"\s*:\s*"([^"]*(?:\\.[^"]*)*)"/);
        if (jsonMatch && jsonMatch[1]) {
          try {
            cleanResponse = JSON.parse(`"${jsonMatch[1]}"`);
          } catch (e) {
            // If parsing fails, try a simpler approach - remove the JSON block
            cleanResponse = cleanResponse
              .replace(/\{[\s\S]*"chartSuggestion"[\s\S]*\}/g, '')
              .replace(/```json[\s\S]*```/g, '')
              .trim();
          }
        }
      }
      
      // If we have a chartSuggestion, add a summary if the response is empty
      if (chartSuggestion && !cleanResponse) {
        cleanResponse = `I've prepared changes for your chart. Click "Apply Changes" to update the query and configuration.`;
      }
      
      // Add AI response to messages
      setAiMessages(prev => [...prev, { 
        role: 'assistant', 
        content: cleanResponse || 'I\'ve generated a suggestion for your chart. Click "Apply Changes" to use it.',
        chartSuggestion
      }]);
      
      // Store the suggestion for applying
      if (chartSuggestion) {
        setAiSuggestion({
          ...chartSuggestion,
          recommendedChartType
        });
      }
    } catch (error) {
      console.error('AI chart edit error:', error);
      setAiMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again or rephrase your request.',
        isError: true
      }]);
    } finally {
      setIsAiLoading(false);
    }
  };
  
  const handleAiKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAiSend();
    }
  };
  
  const applyAiSuggestion = () => {
    if (!aiSuggestion) return;
    
    if (aiSuggestion.query) {
      setQuery(aiSuggestion.query);
    }
    if (aiSuggestion.type) {
      setChartType(aiSuggestion.type);
    }
    if (aiSuggestion.name && aiSuggestion.name !== chartName) {
      // Optionally update name if it's different
      setChartName(aiSuggestion.name);
    }
    if (aiSuggestion.config?.xAxis?.label) {
      setXAxisLabel(aiSuggestion.config.xAxis.label);
    }
    if (aiSuggestion.config?.yAxis?.label) {
      setYAxisLabel(aiSuggestion.config.yAxis.label);
    }
    
    setAiSuggestion(null);
    toast.success('AI suggestions applied! Preview to see the changes.');
    setActiveTab('query');
  };
  
  const hasChanges = chart && (
    chartName !== chart.name ||
    chartType !== chart.type ||
    query !== chart.query ||
    description !== (chart.description || '') ||
    xAxisLabel !== (chart.chartConfig?.xAxis?.label || '') ||
    yAxisLabel !== (chart.chartConfig?.yAxis?.label || '') ||
    dateFilterEnabled !== (chart.dateFilterConfig?.enabled || false) ||
    dateFilterColumn !== (chart.dateFilterConfig?.column || '') ||
    dateFilterColumnType !== (chart.dateFilterConfig?.columnType || 'STRING') ||
    dateFilterDefaultRange !== (chart.dateFilterConfig?.defaultRange || 'last30days')
  );
  
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white dark:bg-surface-900 shadow-2xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-surface-200 dark:border-surface-800">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                      <PencilIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <Dialog.Title className="text-lg font-display font-semibold text-surface-900 dark:text-white">
                        Edit Chart
                      </Dialog.Title>
                      <p className="text-sm text-surface-500">
                        Modify chart settings and query
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={handleClose} 
                    className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5 text-surface-500" />
                  </button>
                </div>
                
                {/* Tabs */}
                <div className="flex border-b border-surface-200 dark:border-surface-800 px-6">
                  <button
                    onClick={() => setActiveTab('basic')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'basic'
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-surface-500 hover:text-surface-700 dark:hover:text-surface-300'
                    }`}
                  >
                    Basic Info
                  </button>
                  <button
                    onClick={() => setActiveTab('query')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'query'
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-surface-500 hover:text-surface-700 dark:hover:text-surface-300'
                    }`}
                  >
                    SQL Query
                  </button>
                  <button
                    onClick={() => setActiveTab('preview')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                      activeTab === 'preview'
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-surface-500 hover:text-surface-700 dark:hover:text-surface-300'
                    }`}
                  >
                    <EyeIcon className="w-4 h-4" />
                    Preview
                  </button>
                  <button
                    onClick={() => setActiveTab('ai')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                      activeTab === 'ai'
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-surface-500 hover:text-surface-700 dark:hover:text-surface-300'
                    }`}
                  >
                    <SparklesIcon className="w-4 h-4" />
                    AI Assistant
                  </button>
                </div>
                
                {/* Content */}
                <div className="p-6 min-h-[400px] max-h-[70vh] overflow-y-auto">
                  {/* Basic Info Tab */}
                  {activeTab === 'basic' && (
                    <div className="space-y-6">
                      <div>
                        <label className="label">Chart Name</label>
                        <input
                          type="text"
                          value={chartName}
                          onChange={(e) => setChartName(e.target.value)}
                          placeholder="Enter chart name"
                          className="input"
                        />
                      </div>
                      
                      <div>
                        <label className="label">Description (optional)</label>
                        <input
                          type="text"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="Brief description of this chart"
                          className="input"
                        />
                      </div>
                      
                      <div>
                        <label className="label">Chart Type</label>
                        <div className="grid grid-cols-4 gap-3">
                          {chartTypes.map(type => (
                            <button
                              key={type.id}
                              onClick={() => setChartType(type.id)}
                              className={`p-3 rounded-xl border-2 text-center transition-all hover:border-primary-500 ${
                                chartType === type.id 
                                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-950' 
                                  : 'border-surface-200 dark:border-surface-700'
                              }`}
                            >
                              <span className="text-xl mb-1 block">{type.icon}</span>
                              <span className="text-xs font-medium text-surface-700 dark:text-surface-300">
                                {type.name}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Axis Labels - only show for chart types with axes */}
                      {!['pie', 'donut', 'table', 'kpi'].includes(chartType) && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="label">X-Axis Label</label>
                            <input
                              type="text"
                              value={xAxisLabel}
                              onChange={(e) => setXAxisLabel(e.target.value)}
                              placeholder="Auto-generated if empty"
                              className="input"
                            />
                            <p className="text-xs text-surface-500 mt-1">
                              Leave empty to auto-generate from data
                            </p>
                          </div>
                          <div>
                            <label className="label">Y-Axis Label</label>
                            <input
                              type="text"
                              value={yAxisLabel}
                              onChange={(e) => setYAxisLabel(e.target.value)}
                              placeholder="Auto-generated if empty"
                              className="input"
                            />
                            <p className="text-xs text-surface-500 mt-1">
                              Leave empty to auto-generate from data
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {/* Date Filter Configuration */}
                      <div className="p-4 bg-surface-50 dark:bg-surface-800 rounded-lg space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-surface-700 dark:text-surface-300">
                              Enable Date Range Filter
                            </label>
                            <p className="text-xs text-surface-500 mt-0.5">
                              Allow users to filter chart data by date range
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setDateFilterEnabled(!dateFilterEnabled)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              dateFilterEnabled ? 'bg-primary-600' : 'bg-surface-300 dark:bg-surface-600'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                dateFilterEnabled ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                        
                        {dateFilterEnabled && (
                          <div className="space-y-3 pt-3 border-t border-surface-200 dark:border-surface-700">
                            <div>
                              <label className="label text-xs">Date Column Name</label>
                              <input
                                type="text"
                                value={dateFilterColumn}
                                onChange={(e) => setDateFilterColumn(e.target.value)}
                                placeholder="e.g., createdAt, order_date, timestamp"
                                className="input text-sm"
                              />
                              <p className="text-xs text-surface-500 mt-1">
                                The column in your query that contains dates
                              </p>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="label text-xs">Column Type</label>
                                <select
                                  value={dateFilterColumnType}
                                  onChange={(e) => setDateFilterColumnType(e.target.value)}
                                  className="input text-sm"
                                >
                                  <option value="STRING">STRING</option>
                                  <option value="TIMESTAMP">TIMESTAMP</option>
                                  <option value="DATETIME">DATETIME</option>
                                  <option value="DATE">DATE</option>
                                </select>
                              </div>
                              <div>
                                <label className="label text-xs">Default Range</label>
                                <select
                                  value={dateFilterDefaultRange}
                                  onChange={(e) => setDateFilterDefaultRange(e.target.value)}
                                  className="input text-sm"
                                >
                                  <option value="today">Today</option>
                                  <option value="yesterday">Yesterday</option>
                                  <option value="last7days">Last 7 Days</option>
                                  <option value="last30days">Last 30 Days</option>
                                  <option value="thisMonth">This Month</option>
                                  <option value="lastMonth">Last Month</option>
                                  <option value="thisYear">This Year</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Data Source Info (read-only) */}
                      <div className="p-4 bg-surface-50 dark:bg-surface-800 rounded-lg">
                        <label className="text-xs font-medium text-surface-500 uppercase tracking-wide">
                          Data Source
                        </label>
                        <p className="text-sm text-surface-700 dark:text-surface-300 mt-1">
                          {chart?.isMart && chart?.martId ? (
                            <span className="flex items-center gap-1">
                              <span className="text-purple-600 dark:text-purple-400">⭐ Mart:</span>
                              {chart.martId.displayName || chart.martId.name || chart.dbtModelName}
                            </span>
                          ) : chart?.isWideView && chart?.wideViewName ? (
                            <span className="flex items-center gap-1">
                              <span className="text-blue-600 dark:text-blue-400">🔗 Wide View:</span>
                              {chart.wideViewName}
                            </span>
                          ) : chart?.dataSourceId?.name || chart?.dataSource?.name || (chart?.aiGenerated ? 'AI Generated (Direct Query)' : 'Unknown')}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Query Tab */}
                  {activeTab === 'query' && (
                    <div className="space-y-4">
                      {/* Mode Toggle */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 bg-surface-100 dark:bg-surface-800 p-1 rounded-lg">
                          <button
                            onClick={() => setMode('visual')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                              mode === 'visual'
                                ? 'bg-white dark:bg-surface-700 text-primary-600 dark:text-primary-400 shadow-sm'
                                : 'text-surface-600 dark:text-surface-400 hover:text-surface-800 dark:hover:text-surface-200'
                            }`}
                          >
                            <CubeIcon className="w-4 h-4" />
                            Visual Builder
                          </button>
                          <button
                            onClick={() => setMode('sql')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                              mode === 'sql'
                                ? 'bg-white dark:bg-surface-700 text-primary-600 dark:text-primary-400 shadow-sm'
                                : 'text-surface-600 dark:text-surface-400 hover:text-surface-800 dark:hover:text-surface-200'
                            }`}
                          >
                            <CodeBracketIcon className="w-4 h-4" />
                            SQL Editor
                          </button>
                        </div>
                        <button
                          onClick={handlePreview}
                          disabled={isPreviewLoading || !query.trim()}
                          className="btn-secondary text-sm py-1.5 px-3"
                        >
                          {isPreviewLoading ? (
                            <>
                              <ArrowPathIcon className="w-4 h-4 mr-1.5 animate-spin" />
                              Loading...
                            </>
                          ) : (
                            <>
                              <EyeIcon className="w-4 h-4 mr-1.5" />
                              Preview Results
                            </>
                          )}
                        </button>
                      </div>
                      
                      {/* Visual Builder */}
                      {mode === 'visual' ? (
                        <div className="space-y-4">
                          <VisualChartBuilder
                            schema={schema}
                            chartType={chartType}
                            sourceType={sourceType}
                            tableName={tableName}
                            onQueryGenerated={handleVisualQueryGenerated}
                            initialConfig={visualConfig}
                          />
                          
                          {/* Show generated SQL */}
                          {query && (
                            <div className="bg-surface-50 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-surface-500 uppercase tracking-wide">
                                  Generated SQL
                                </span>
                                <button
                                  onClick={() => setMode('sql')}
                                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                                >
                                  Edit directly
                                </button>
                              </div>
                              <pre className="text-xs font-mono text-surface-700 dark:text-surface-300 whitespace-pre-wrap overflow-x-auto">
                                {query}
                              </pre>
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <textarea
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="SELECT * FROM ${dataset}.table_name"
                            rows={12}
                            className="input font-mono text-sm"
                            style={{ minHeight: '300px' }}
                          />
                          
                          <p className="text-xs text-surface-500">
                            Use <code className="bg-surface-100 dark:bg-surface-800 px-1 py-0.5 rounded">{'${dataset}'}</code> as the dataset placeholder. 
                            It will be replaced with your company's BigQuery dataset.
                          </p>
                        </>
                      )}
                      
                      {previewError && (
                        <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
                          <p className="text-sm text-red-700 dark:text-red-300">{previewError}</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Preview Tab */}
                  {activeTab === 'preview' && (
                    <div className="h-[400px]">
                      {isPreviewLoading ? (
                        <div className="h-full flex items-center justify-center">
                          <div className="text-center">
                            <div className="w-10 h-10 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin mx-auto mb-3" />
                            <p className="text-sm text-surface-500">Running query...</p>
                          </div>
                        </div>
                      ) : previewError ? (
                        <div className="h-full flex items-center justify-center">
                          <div className="text-center max-w-md">
                            <div className="p-4 bg-red-50 dark:bg-red-950/30 rounded-lg">
                              <p className="text-sm text-red-700 dark:text-red-300">{previewError}</p>
                            </div>
                            <button
                              onClick={() => setActiveTab('query')}
                              className="mt-4 btn-secondary text-sm"
                            >
                              Edit Query
                            </button>
                          </div>
                        </div>
                      ) : !previewData ? (
                        <div className="h-full flex items-center justify-center">
                          <div className="text-center">
                            <EyeIcon className="w-12 h-12 text-surface-300 dark:text-surface-600 mx-auto mb-3" />
                            <p className="text-surface-500 mb-4">No preview available</p>
                            <button
                              onClick={handlePreview}
                              disabled={!query.trim()}
                              className="btn-primary text-sm"
                            >
                              Generate Preview
                            </button>
                          </div>
                        </div>
                      ) : chartType === 'table' ? (
                        <div className="h-full overflow-auto border border-surface-200 dark:border-surface-700 rounded-lg">
                          <table className="w-full text-sm">
                            <thead className="bg-surface-50 dark:bg-surface-800 sticky top-0">
                              <tr>
                                {previewData?.[0] && Object.keys(previewData[0]).map(key => (
                                  <th key={key} className="px-4 py-2 text-left font-medium text-surface-600 dark:text-surface-400 border-b border-surface-200 dark:border-surface-700">
                                    {key}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {previewData?.slice(0, 50).map((row, i) => (
                                <tr key={i} className="border-b border-surface-100 dark:border-surface-800">
                                  {Object.values(row).map((value, j) => (
                                    <td key={j} className="px-4 py-2 text-surface-700 dark:text-surface-300">
                                      {String(value)}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : chartType === 'kpi' ? (
                        <div className="h-full flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-5xl font-display font-bold text-surface-900 dark:text-white">
                              {previewData?.[0] ? Object.values(previewData[0])[0] : '—'}
                            </div>
                            <p className="text-surface-500 mt-2">
                              {previewData?.[0] ? Object.keys(previewData[0])[0] : 'Value'}
                            </p>
                          </div>
                        </div>
                      ) : previewOption ? (
                        <ReactECharts
                          option={previewOption}
                          style={{ height: '100%', width: '100%' }}
                          opts={{ renderer: 'svg' }}
                        />
                      ) : (
                        <div className="h-full flex items-center justify-center text-surface-500">
                          Unable to generate chart preview
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* AI Assistant Tab */}
                  {activeTab === 'ai' && (
                    <div className="h-[500px] flex flex-col">
                      {/* Chat Messages */}
                      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-surface-50 dark:bg-surface-800/30 rounded-lg">
                        {aiMessages.length === 0 ? (
                          <div className="h-full flex flex-col items-center justify-center text-center">
                            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mb-4">
                              <SparklesIcon className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-lg font-display font-bold text-surface-900 dark:text-white mb-2">
                              How can I help you edit this chart?
                            </h3>
                            <p className="text-surface-500 max-w-sm mb-6">
                              Describe the changes you want to make, and I'll update the SQL query and configuration.
                            </p>
                            
                            {/* Suggestion chips */}
                            <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                              {[
                                'Change to a line chart',
                                'Add a filter for last 30 days',
                                'Group by month instead',
                                'Show top 10 only',
                                'Add a comparison to previous period'
                              ].map((suggestion, i) => (
                                <button
                                  key={i}
                                  onClick={() => setAiInput(suggestion)}
                                  className="px-3 py-2 rounded-full bg-white dark:bg-surface-700 text-sm text-surface-600 dark:text-surface-400 hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/30 dark:hover:text-primary-400 transition-colors border border-surface-200 dark:border-surface-600"
                                >
                                  {suggestion}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <>
                            {aiMessages.map((msg, i) => (
                              <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  msg.role === 'assistant' 
                                    ? 'bg-gradient-to-br from-primary-500 to-accent-500' 
                                    : 'bg-surface-200 dark:bg-surface-700'
                                }`}>
                                  {msg.role === 'assistant' 
                                    ? <SparklesIcon className="w-4 h-4 text-white" />
                                    : <UserIcon className="w-4 h-4 text-surface-600 dark:text-surface-400" />
                                  }
                                </div>
                                
                                <div className={`max-w-[85%] ${msg.role === 'user' ? 'text-right' : ''}`}>
                                  <div className={`inline-block px-4 py-3 rounded-2xl ${
                                    msg.role === 'assistant'
                                      ? msg.isError 
                                        ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                        : 'bg-white dark:bg-surface-800 text-surface-900 dark:text-white shadow-sm'
                                      : 'bg-primary-600 text-white'
                                  }`}>
                                    {msg.role === 'assistant' ? (
                                      <div className="prose prose-sm dark:prose-invert max-w-none">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                          {msg.content}
                                        </ReactMarkdown>
                                      </div>
                                    ) : (
                                      <p className="whitespace-pre-wrap">{msg.content}</p>
                                    )}
                                  </div>
                                  
                                  {/* Apply suggestion button */}
                                  {msg.role === 'assistant' && msg.chartSuggestion && (
                                    <div className="mt-2">
                                      <button
                                        onClick={() => {
                                          // Apply the suggestion directly from the message
                                          const suggestion = msg.chartSuggestion;
                                          if (suggestion.query) setQuery(suggestion.query);
                                          if (suggestion.type) setChartType(suggestion.type);
                                          if (suggestion.name) setChartName(suggestion.name);
                                          if (suggestion.config?.xAxis?.label) setXAxisLabel(suggestion.config.xAxis.label);
                                          if (suggestion.config?.yAxis?.label) setYAxisLabel(suggestion.config.yAxis.label);
                                          toast.success('AI suggestions applied! Preview to see the changes.');
                                          setActiveTab('query');
                                        }}
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
                                      >
                                        <LightBulbIcon className="w-4 h-4" />
                                        Apply Changes
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                            
                            {isAiLoading && (
                              <div className="flex gap-3">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                                  <SparklesIcon className="w-4 h-4 text-white" />
                                </div>
                                <div className="bg-white dark:bg-surface-800 rounded-2xl px-4 py-3 shadow-sm">
                                  <div className="flex gap-1">
                                    <div className="w-2 h-2 bg-surface-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                    <div className="w-2 h-2 bg-surface-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                    <div className="w-2 h-2 bg-surface-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                        <div ref={aiMessagesEndRef} />
                      </div>
                      
                      {/* Pending suggestion banner */}
                      {aiSuggestion && (
                        <div className="mx-4 mt-3 p-3 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 rounded-lg flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <LightBulbIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                            <span className="text-sm text-primary-700 dark:text-primary-300">
                              AI suggestion ready to apply
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setAiSuggestion(null)}
                              className="px-3 py-1 text-sm text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-md transition-colors"
                            >
                              Dismiss
                            </button>
                            <button
                              onClick={applyAiSuggestion}
                              className="px-3 py-1 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {/* Chat Input */}
                      <div className="mt-4 flex gap-3">
                        <textarea
                          value={aiInput}
                          onChange={(e) => setAiInput(e.target.value)}
                          onKeyDown={handleAiKeyDown}
                          placeholder="Describe changes to your chart..."
                          rows={2}
                          className="flex-1 input resize-none"
                        />
                        <button
                          onClick={handleAiSend}
                          disabled={!aiInput.trim() || isAiLoading}
                          className="btn-primary self-end px-4"
                        >
                          <PaperAirplaneIcon className="w-5 h-5" />
                        </button>
                      </div>
                      <p className="text-xs text-surface-500 mt-2">
                        Press Enter to send • Shift+Enter for new line
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-surface-200 dark:border-surface-800 bg-surface-50 dark:bg-surface-800/50">
                  <div className="text-sm text-surface-500">
                    {hasChanges ? (
                      <span className="text-amber-600 dark:text-amber-400">• Unsaved changes</span>
                    ) : (
                      <span>No changes</span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleClose}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={isLoading || !hasChanges || !chartName.trim() || !query.trim()}
                      className="btn-primary"
                    >
                      {isLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

